import React, { ReactElement, useCallback, useContext, useMemo, useRef, useState } from "react";
import { QuickCheckoutCartApiStatus, UpdateQuickCheckoutDataPayload } from "@swiggy-private/cart-types";
import {
    CheckoutActionTypes,
    CheckoutContext,
    FEATURE_NR_CALL_STATUS,
    QUICK_CHECKOUT_HYDRATE_TRANSPORT,
    recordQuickCheckoutCartHydrate,
    recordQuickCheckoutCartHydrateDrop,
    recordQuickCheckoutCartHydrateReject,
} from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

import { CartContext } from "@cart-client/contexts/CartContext";
import { QuickCheckoutContext } from "@cart-client/contexts/QuickCheckoutContext";
import {
    buildQuickCheckoutUpdateCartAction,
    clearQuickCheckoutPrefetch,
    getQuickCheckoutContentHeight,
    getQuickCheckoutWebviewScale,
    isNewerQuickCheckoutCartUpdate,
    parseQuickCheckoutCartUpdateTimestamp,
} from "@cart-client/helpers/QuickCheckoutCart";
import { useQuickCheckoutGlobalJSBridge } from "@cart-client/hooks/useQuickCheckoutGlobalJSBridge";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

export function QuickCheckoutContextProvider(props: { children: React.ReactNode }): ReactElement {
    const { checkoutState } = useContext(CheckoutContext);
    const { cartState, setCartState } = useContext(CartContext);
    const reviewedCart = cartState.reviewedCart as MROReviewedCart | null;
    // Unified update-cart lifecycle status. Starts as LOADING until the direct-bridge
    // prefetch hydration (or the JS bridge) reports the first status. Both flows write
    // here so the enum never drifts and the renderer reads a single source of truth.
    const [updateCartApiState, setUpdateCartApiState] = useState<QuickCheckoutCartApiStatus>(
        QuickCheckoutCartApiStatus.LOADING,
    );

    // Refs (not state): compared synchronously across prefetch hydrate and JS-bridge
    // callbacks so a stale success cannot win a race against a fresher one mid-render.
    const lastAppliedCartUpdateAtRef = useRef<number | null>(null);
    const jsBridgeLifecycleUpdateSeenRef = useRef(false);

    const getLastAppliedCartUpdateAt = useCallback(() => lastAppliedCartUpdateAtRef.current, []);

    const setLastAppliedCartUpdateAt = useCallback((timestampMs: number) => {
        lastAppliedCartUpdateAtRef.current = timestampMs;
    }, []);

    const hasJsBridgeLifecycleUpdate = useCallback(() => jsBridgeLifecycleUpdateSeenRef.current, []);

    // in quick checkout flow, user can move between full cart and partial webview flows
    // some operations need us to reload carts like address change, login, generic errors etc
    // in such cases if user is on full cart view we can reload that cart view then and there
    // but when user comes back to quick checkout flow we need to reload the parent screen to show the updated cart
    // for quick checkout as the parent screen in a app collection page we need to pass signal to apps on remount
    const [refreshParentScreenOnRemount, setRefreshParentScreenOnRemount] = useState(false);

    // App reported SUCCESS but sent nothing renderable. Returning silently would leave the
    // previous status (usually "loading") on screen with no way out, so move to FAILED and let
    // the renderer show the generic error + retry.
    const handleQuickCheckoutSuccessWithoutCart = useCallback(() => {
        setUpdateCartApiState(QuickCheckoutCartApiStatus.FAILED);
    }, []);

    const handleQuickCheckoutUpdate = useCallback(
        (data: UpdateQuickCheckoutDataPayload) => {
            // Dropped, not queued: outside quick checkout the full page cart owns update-cart and
            // writes the newer cart into the same CartContext, so replaying a stale app payload
            // later would overwrite it
            if (checkoutState.type !== CheckoutActionTypes.SHOW_QUICK_CHECKOUT) {
                return;
            }

            jsBridgeLifecycleUpdateSeenRef.current = true;

            // loading/failed are status-only and always apply. success is timestamp-gated so a
            // stale bridge payload cannot flip the UI to success without a fresher cart.
            if (data.cart_update_status !== QuickCheckoutCartApiStatus.SUCCESS) {
                setUpdateCartApiState(data.cart_update_status);
                recordQuickCheckoutCartHydrate({
                    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT.JS_BRIDGE,
                    status:
                        data.cart_update_status === QuickCheckoutCartApiStatus.FAILED
                            ? FEATURE_NR_CALL_STATUS.FAILURE
                            : FEATURE_NR_CALL_STATUS.LOADING,
                    cartUpdateStatus: data.cart_update_status,
                });
                return;
            }

            // SUCCESS without last_updated_timestamp is malformed — contract requires a timestamp so
            // clients can order competing updates. Reject rather than apply with a fallback clock.
            const lastAppliedTimestamp = lastAppliedCartUpdateAtRef.current;
            const currentTimestamp = data.last_updated_timestamp;
            const acceptedTs = parseQuickCheckoutCartUpdateTimestamp(currentTimestamp);
            if (acceptedTs === null) {
                captureError(
                    new Error("QuickCheckout success update rejected: missing or invalid last_updated_timestamp"),
                );
                recordQuickCheckoutCartHydrateReject({
                    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT.JS_BRIDGE,
                    reason: "missing_last_updated_timestamp",
                    lastAppliedTimestamp,
                    currentTimestamp,
                });
                return;
            }

            if (!isNewerQuickCheckoutCartUpdate(acceptedTs, lastAppliedTimestamp)) {
                recordQuickCheckoutCartHydrateDrop({
                    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT.JS_BRIDGE,
                    reason: "stale_timestamp",
                    lastAppliedTimestamp,
                    currentTimestamp: acceptedTs,
                });
                return;
            }

            const updateCartAction = buildQuickCheckoutUpdateCartAction(
                data.cart_response as unknown as MROReviewedCart,
                data.local_cart ?? null,
            );
            if (isEmpty(updateCartAction)) {
                recordQuickCheckoutCartHydrateDrop({
                    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT.JS_BRIDGE,
                    reason: "empty_cart_action",
                    lastAppliedTimestamp,
                    currentTimestamp: acceptedTs,
                });
                handleQuickCheckoutSuccessWithoutCart();
                return;
            }

            setUpdateCartApiState(QuickCheckoutCartApiStatus.SUCCESS);
            setCartState(updateCartAction);
            lastAppliedCartUpdateAtRef.current = acceptedTs;
            // Drop any leftover prefetch handoff (e.g. stale "loading") so QC remount
            // prefers CartContext / existing-cart path instead of replaying window status.
            clearQuickCheckoutPrefetch();
            recordQuickCheckoutCartHydrate({
                transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT.JS_BRIDGE,
                status: FEATURE_NR_CALL_STATUS.SUCCESS,
                cartUpdateStatus: QuickCheckoutCartApiStatus.SUCCESS,
                lastAppliedTimestamp,
                currentTimestamp: acceptedTs,
            });
        },
        [checkoutState.type, setCartState, handleQuickCheckoutSuccessWithoutCart],
    );

    // Keep the context in sync with the native "update quick checkout footer" JS bridge.
    useQuickCheckoutGlobalJSBridge({
        onQuickCheckoutUpdate: handleQuickCheckoutUpdate,
        onSuccessWithoutCart: handleQuickCheckoutSuccessWithoutCart,
    });

    const isLoading = updateCartApiState === QuickCheckoutCartApiStatus.LOADING;
    // Keep cart/loading snapshots current for getQcFooterType() with no args (post-render reads).
    const reviewedCartRef = useRef(reviewedCart);
    const isLoadingRef = useRef(isLoading);
    reviewedCartRef.current = reviewedCart;
    isLoadingRef.current = isLoading;

    // Single calculation site for QC footer scale. Optional cart override is for await doUpdateCart to recalculate updated qcfootertype
    // continuations that must not wait for the next render to refresh qcFooterType.
    const getQcFooterType = useCallback((cart?: MROReviewedCart | null, loading?: boolean) => {
        const resolvedCart = cart !== undefined ? cart : reviewedCartRef.current;
        // A cart override is a completed update-cart response — not a first-load shimmer.
        let resolvedLoading = isLoadingRef.current;
        if (loading !== undefined) {
            resolvedLoading = loading;
        } else if (cart !== undefined) {
            resolvedLoading = false;
        }
        return getQuickCheckoutWebviewScale(resolvedCart, resolvedLoading);
    }, []);

    const qcFooterType = useMemo(
        () => getQcFooterType(reviewedCart, isLoading),
        [getQcFooterType, reviewedCart, isLoading],
    );
    const qcFooterContentHeight = useMemo(
        () => getQuickCheckoutContentHeight(reviewedCart, isLoading),
        [reviewedCart, isLoading],
    );

    const value = useMemo(
        () => ({
            updateCartApiState,
            setUpdateCartApiState,
            refreshParentScreenOnRemount,
            setRefreshParentScreenOnRemount,
            getLastAppliedCartUpdateAt,
            setLastAppliedCartUpdateAt,
            hasJsBridgeLifecycleUpdate,
            qcFooterType,
            getQcFooterType,
            qcFooterContentHeight,
        }),
        [
            updateCartApiState,
            refreshParentScreenOnRemount,
            setRefreshParentScreenOnRemount,
            getLastAppliedCartUpdateAt,
            setLastAppliedCartUpdateAt,
            hasJsBridgeLifecycleUpdate,
            qcFooterType,
            getQcFooterType,
            qcFooterContentHeight,
        ],
    );

    return <QuickCheckoutContext.Provider value={value}>{props.children}</QuickCheckoutContext.Provider>;
}
